/**
 * RehearsalsTab — new tab in the Admin Worship Hub for choir rehearsal
 * planning. Lists rehearsals, lets an admin create one with SATB
 * assignments, and mark attendance in-place.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  CalendarClock, PlusCircle, Users, UserCheck, UserX, Clock, Search, Trash2,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const W = `${API}/api/admin/worship`;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const VOICE_LABELS = { soprano: 'Soprano', alto: 'Alto', tenor: 'Tenor', bass: 'Bass' };
const STATUS_ICONS = { present: UserCheck, absent: UserX, late: Clock, excused: Users };
const STATUS_COLORS = {
  present: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  absent: 'text-rose-300 border-rose-500/40 bg-rose-500/10',
  late: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  excused: 'text-slate-300 border-slate-500/40 bg-slate-500/10',
};

export default function RehearsalsTab() {
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const listQ = useQuery({
    queryKey: ['worship-rehearsals'],
    queryFn: async () => (await axios.get(`${W}/rehearsals?limit=100`, { headers: authH() })).data,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-fuchsia-300" /> Choir Rehearsals
          </h3>
          <p className="text-[10px] text-slate-500">
            SATB assignments + attendance. Attendance rows update as members join.
          </p>
        </div>
        <Button size="sm" data-testid="new-rehearsal-btn"
                className="bg-fuchsia-600 hover:bg-fuchsia-500"
                onClick={() => setShowCreate(true)}>
          <PlusCircle className="mr-1 h-3 w-3" /> New Rehearsal
        </Button>
      </div>

      <div className="space-y-2">
        {(listQ.data?.items || []).length === 0 && !listQ.isLoading && (
          <p className="text-center text-xs text-slate-500 py-6">No rehearsals scheduled yet.</p>
        )}
        {(listQ.data?.items || []).map((r) => (
          <RehearsalRow key={r.id} rehearsal={r}
                        expanded={expandedId === r.id}
                        onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)} />
        ))}
      </div>

      {showCreate && <CreateRehearsalDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}


function RehearsalRow({ rehearsal, expanded, onToggle }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: async () => (await axios.delete(
      `${W}/rehearsals/${rehearsal.id}`, { headers: authH() },
    )).data,
    onSuccess: () => { toast.success('Rehearsal deleted'); qc.invalidateQueries({ queryKey: ['worship-rehearsals'] }); },
  });

  const attendanceByPerson = new Map((rehearsal.attendance || []).map((a) => [a.person_id, a]));
  const groupedAssignments = groupByVoicePart(rehearsal.assignments || []);
  const totalAssigned = (rehearsal.assignments || []).length;
  const presentCount = (rehearsal.attendance || []).filter((a) => a.status === 'present').length;

  return (
    <div className="rounded border border-slate-800 bg-slate-950/70 p-2 text-sm" data-testid={`rehearsal-${rehearsal.id}`}>
      <div className="flex items-center justify-between">
        <button type="button" onClick={onToggle} className="text-left">
          <div className="font-medium text-slate-100">{rehearsal.title}</div>
          <div className="text-[10px] text-slate-500">
            {rehearsal.scheduled_at ? new Date(rehearsal.scheduled_at).toLocaleString() : 'unscheduled'}
            {rehearsal.location && ` · ${rehearsal.location}`}
            <span className="ml-2 text-emerald-300">
              {totalAssigned} assigned · {presentCount} present
            </span>
          </div>
        </button>
        <div className="flex gap-1">
          <button type="button" data-testid={`rehearsal-toggle-${rehearsal.id}`} onClick={onToggle}
                  className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-slate-200 hover:border-slate-500">
            {expanded ? 'Hide' : 'Open'}
          </button>
          <button type="button" data-testid={`rehearsal-delete-${rehearsal.id}`} onClick={() => del.mutate()}
                  className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-rose-300 hover:border-rose-500">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2 space-y-2">
          {['soprano', 'alto', 'tenor', 'bass'].map((vp) => (
            <div key={vp} className="rounded border border-slate-800 bg-slate-950 p-2" data-testid={`rehearsal-${rehearsal.id}-part-${vp}`}>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-fuchsia-200">
                  {VOICE_LABELS[vp]} ({(groupedAssignments[vp] || []).length})
                </span>
              </div>
              {(groupedAssignments[vp] || []).length === 0 && (
                <p className="text-[10px] text-slate-500">No one assigned to {VOICE_LABELS[vp].toLowerCase()}.</p>
              )}
              <div className="space-y-1">
                {(groupedAssignments[vp] || []).map((a, i) => (
                  <MemberRow
                    key={`${a.person_id}-${i}`}
                    rehearsalId={rehearsal.id}
                    member={a}
                    attendance={attendanceByPerson.get(a.person_id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function MemberRow({ rehearsalId, member, attendance }) {
  const qc = useQueryClient();
  const mark = useMutation({
    mutationFn: async (status) => (await axios.post(
      `${W}/rehearsals/${rehearsalId}/attendance`,
      { person_id: member.person_id || member.person_email || member.person_name,
        status, notes: null },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['worship-rehearsals'] }),
  });
  const currentStatus = attendance?.status;
  return (
    <div className="flex items-center justify-between rounded border border-slate-800 bg-slate-900 px-2 py-1 text-xs" data-testid={`rehearsal-member-${member.person_id || member.person_name}`}>
      <div>
        <div className="text-slate-100">{member.person_name || member.person_email}</div>
        {currentStatus && (
          <Badge variant="outline" className={`mt-0.5 text-[9px] ${STATUS_COLORS[currentStatus]}`}>
            {currentStatus}
          </Badge>
        )}
      </div>
      <div className="flex gap-1">
        {['present', 'late', 'excused', 'absent'].map((st) => {
          const Icon = STATUS_ICONS[st];
          const active = currentStatus === st;
          return (
            <button
              key={st}
              type="button"
              data-testid={`mark-${st}-${member.person_id || member.person_name}`}
              onClick={() => mark.mutate(st)}
              className={`rounded border px-1.5 py-0.5 text-[9px] ${active ? STATUS_COLORS[st] : 'border-slate-700 bg-slate-950 text-slate-400 hover:border-slate-500'}`}
              title={st}
            >
              <Icon className="h-3 w-3" />
            </button>
          );
        })}
      </div>
    </div>
  );
}


function groupByVoicePart(assignments) {
  const out = { soprano: [], alto: [], tenor: [], bass: [] };
  for (const a of assignments) {
    if (out[a.voice_part]) out[a.voice_part].push(a);
  }
  return out;
}


function CreateRehearsalDialog({ onClose }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [voicePart, setVoicePart] = useState('soprano');
  const [q, setQ] = useState('');

  const searchQ = useQuery({
    queryKey: ['worship-volunteers', q],
    queryFn: async () => (await axios.get(`${W}/volunteers/search?q=${encodeURIComponent(q)}`, { headers: authH() })).data,
  });

  const create = useMutation({
    mutationFn: async () => (await axios.post(
      `${W}/rehearsals`,
      {
        title,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        location,
        notes,
        assignments,
        piece_song_ids: [],
      },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('Rehearsal scheduled');
      qc.invalidateQueries({ queryKey: ['worship-rehearsals'] });
      onClose();
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  const addAssignment = (person) => {
    if (assignments.some((a) => a.person_id === person.id && a.voice_part === voicePart)) return;
    setAssignments((prev) => [...prev, {
      voice_part: voicePart,
      person_id: person.id,
      person_name: person.name,
      person_email: person.email,
    }]);
  };
  const removeAssignment = (i) => setAssignments((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="rehearsal-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule Choir Rehearsal</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            SATB voice-part assignments + optional schedule and location.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="rehearsal-title" placeholder="Title (e.g. Sunday choir warm-up)"
                 value={title} onChange={(e) => setTitle(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="rehearsal-schedule" type="datetime-local"
                   value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
            <Input data-testid="rehearsal-location" placeholder="Location (choir room, etc.)"
                   value={location} onChange={(e) => setLocation(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
          </div>
          <Input data-testid="rehearsal-notes" placeholder="Notes for the choir…"
                 value={notes} onChange={(e) => setNotes(e.target.value)}
                 className="bg-slate-950 border-slate-700" />

          <div className="rounded border border-slate-800 bg-slate-950 p-2">
            <div className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">
              Assign members (SATB)
            </div>
            <div className="flex items-center gap-2 mb-2">
              <select value={voicePart} onChange={(e) => setVoicePart(e.target.value)}
                      data-testid="rehearsal-voice-part"
                      className="h-8 rounded border border-slate-700 bg-slate-900 px-2 text-xs">
                {['soprano', 'alto', 'tenor', 'bass'].map((v) => (
                  <option key={v} value={v}>{VOICE_LABELS[v]}</option>
                ))}
              </select>
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       data-testid="rehearsal-search"
                       placeholder="Search choir member…"
                       className="pl-8 bg-slate-900 border-slate-700 h-8" />
              </div>
            </div>
            <div className="max-h-32 overflow-y-auto rounded border border-slate-800 bg-slate-950">
              {(searchQ.data?.items || []).slice(0, 8).map((p) => (
                <button key={p.id} type="button" onClick={() => addAssignment(p)}
                        data-testid={`rehearsal-add-${p.id}`}
                        className="flex w-full items-center justify-between border-b border-slate-800 px-2 py-1 text-left text-[11px] hover:bg-slate-800/60">
                  <span className="text-slate-100">{p.name}</span>
                  <span className="text-[9px] text-fuchsia-300">+ {VOICE_LABELS[voicePart]}</span>
                </button>
              ))}
            </div>

            <div className="mt-2 space-y-1">
              {assignments.map((a, i) => (
                <div key={i} data-testid={`rehearsal-assignment-${i}`}
                     className="flex items-center justify-between rounded border border-slate-800 bg-slate-900 px-2 py-1 text-[11px]">
                  <div>
                    <Badge variant="outline" className="border-fuchsia-500/40 text-fuchsia-200 text-[9px] mr-1">
                      {VOICE_LABELS[a.voice_part]}
                    </Badge>
                    <span className="text-slate-100">{a.person_name}</span>
                  </div>
                  <button type="button" onClick={() => removeAssignment(i)}
                          className="text-slate-500 hover:text-rose-400 text-[9px]">×</button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="rehearsal-submit"
                    className="bg-fuchsia-600 hover:bg-fuchsia-500"
                    disabled={!title.trim() || create.isPending}
                    onClick={() => create.mutate()}>
              {create.isPending ? 'Saving…' : 'Schedule Rehearsal'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
